import { ArrowLeft } from "lucide-react";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Drawer,
  Group,
  Space,
  Tabs,
  Text,
} from "@mantine/core";
import { AuthContext } from "../../../providers/AuthProvider";
import PageHeading from "../../../themes/components/PageHeading";
import EmployeeTable from "../components/EmployeeTable";
import TagsTable from "../../tags/components/TagsTable";
import EmployeeImportForm from "../components/EmployeeImportForm";
import EmployeeImportHistoryTable from "../components/EmployeeImportHistoryTable";
import TagForm from "../../tags/components/TagForm";
import PageContainer from "../../../themes/components/PageContainer";
import useEmployeeUploadApiService from "../services/api/EmployeeUploadApiSerivce";
import Employee from "../services/model/Employee";
import Tag from "../../tags/services/model/Tag";
import EmployeeDataImportListApiResponse from "../services/model/EmployeeDataImportListApiResponse";
import EmployeeBulkStatusUpdate from "../components/EmployeeBulkStatusUpdate";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import UserAccessControlUtils, {
  FEATURE_SEGMENTS,
} from "../../../utils/UserAccessControlUtils";
import EmployeeImportJobTable from "../components/EmployeeImportJobTable";
import EmployeeImportJob from "../services/model/EmployeeImportJob";
import SegmentTable from "../../segments/components/SegmentTable";
import SegmentCreateContent from "../../segments/components/SegmentCreateContent";
import SegmentViewContent from "../../segments/components/SegmentViewContent";
import SegmentHistoryContent from "../../segments/components/SegmentHistoryContent";
import SegmentEmployeesContent from "../../segments/components/SegmentEmployeesContent";
import Segment from "../../segments/services/model/Segment";

type SegmentView = "list" | "create" | "history" | "view" | "employees";

type EmployeeDirectoryTabProps = {
  id: number;
  name: string;
  path: string;
  content: ReactNode;
  actions: ReactNode[];
  filters?: ReactNode[];
};

const EmployeeDirectoryPage: React.FC = () => {
  const auth = useContext(AuthContext);
  const { hasPermissions } = React.useContext(AccessPermissionContext);

  const flagEmployeeImporterTools = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYEE_IMPORTER_TOOLS
  );
  const flagEmployeeImporterToolsV2 = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYEE_IMPORTER_TOOLS_V2
  );
  const flagSegments = useEnabledFeatureFlag(FeatureFlags.SEGMENTS);
  const hasSegmentsFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features || [],
    FEATURE_SEGMENTS
  );

  if (!hasPermissions) {
    return <NotFound />;
  }

  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [employeeSelection, setEmployeeSelection] = useState<Employee[]>([]);
  const [tagSelection, setTagSelection] = useState<Tag[]>([]);
  const [employeeImportSelection, setEmployeeImportSelection] = useState<
    EmployeeDataImportListApiResponse[]
  >([]);
  const [employeeImportNewSelection, setEmployeeImportNewSelection] = useState<
    EmployeeImportJob[]
  >([]);
  const [isTagDrawerOpened, setIsTagDrawerOpened] = useState(false);
  const [viewingSegment, setViewingSegment] = useState<Segment | null>(null);
  const [segmentView, setSegmentView] = useState<SegmentView>("list");
  const [tabs, setTabs] = useState<EmployeeDirectoryTabProps[]>([
    {
      id: 0,
      name: "Users",
      path: "#users",
      content: (
        <EmployeeTable
          selection={employeeSelection}
          onSelection={setEmployeeSelection}
        />
      ),
      actions: [
        <span key="import-data-button" className="hidden sm:block ml-3">
          <Button
            variant="filled"
            type="button"
            color="jem-theme"
            onClick={() => setOpened(true)}
          >
            Import Data
          </Button>
        </span>,
        <EmployeeBulkStatusUpdate key="bulk-status-update-button" />,
      ],
    },
    {
      id: 1,
      name: "Tags",
      path: "#tags",
      content: (
        <TagsTable onSelection={setTagSelection} selection={tagSelection} />
      ),
      actions: [
        <Button
          key="create-tags-button"
          variant="filled"
          type="button"
          color="jem-theme"
          onClick={() => setIsTagDrawerOpened(true)}
        >
          Create Tags
        </Button>,
      ],
    },

    {
      id: 2,
      name: "Imports",
      path: "#imports",
      content: (
        <EmployeeImportHistoryTable
          onSelection={setEmployeeImportSelection}
          selection={employeeImportSelection}
        />
      ),
      actions: [
        <span key="import-data-button" className="hidden sm:block ml-3">
          <Button
            key="import-data-button"
            variant="filled"
            type="button"
            color="jem-theme"
            onClick={() => setOpened(true)}
          >
            Import Data
          </Button>
        </span>,
        <EmployeeBulkStatusUpdate key="bulk-status-update-button" />,
      ],
    },
  ]);

  useEffect(() => {
    if (flagEmployeeImporterTools || flagEmployeeImporterToolsV2) {
      const commonActions = [
        <Button
          key="new-import-data-button"
          variant="filled"
          type="button"
          color="jem-theme"
          onClick={() =>
            flagEmployeeImporterToolsV2
              ? navigate("/employee-data-importer-v2")
              : navigate("/employee-data-importer")
          }
        >
          Import Data
        </Button>,
        <EmployeeBulkStatusUpdate key="bulk-status-update-button" />,
      ];

      const flatfileUserPage = tabs.filter((tab) => tab.id === 0)[0];
      if (flatfileUserPage) {
        flatfileUserPage.actions = commonActions;
      }

      const flatfileImportsPage = tabs.filter((tab) => tab.id === 2)[0];

      if (flatfileImportsPage) {
        flatfileImportsPage.name = "Imports";
        flatfileImportsPage.content = (
          <EmployeeImportJobTable
            onSelection={setEmployeeImportNewSelection}
            selection={employeeImportNewSelection}
          />
        );

        flatfileImportsPage.actions = commonActions;
      }

      setTabs((prev) => [
        flatfileUserPage,
        ...prev.filter((tab) => tab.id !== 0 && tab.id !== 2),
        flatfileImportsPage,
      ]);
    }
  }, [flagEmployeeImporterTools, flagEmployeeImporterToolsV2]);

  // Render segmentation content based on view state
  const renderSegmentationContent = (): ReactNode => {
    switch (segmentView) {
      case "create":
        return (
          <SegmentCreateContent
            onBack={() => setSegmentView("list")}
            onSuccess={() => setSegmentView("list")}
          />
        );
      case "view":
        return viewingSegment ? (
          <SegmentViewContent
            segment={viewingSegment}
            onBack={() => {
              setSegmentView("list");
              setViewingSegment(null);
            }}
          />
        ) : null;
      case "employees":
        return viewingSegment ? (
          <SegmentEmployeesContent
            segment={viewingSegment}
            onBack={() => {
              setSegmentView("list");
              setViewingSegment(null);
            }}
          />
        ) : null;
      case "history":
        return <SegmentHistoryContent onBack={() => setSegmentView("list")} />;
      default:
        return (
          <SegmentTable
            onView={(segment) => {
              setViewingSegment(segment);
              setSegmentView("view");
            }}
            onViewEmployees={(segment) => {
              setViewingSegment(segment);
              setSegmentView("employees");
            }}
            onCreateSegment={() => setSegmentView("create")}
            onViewHistory={() => setSegmentView("history")}
          />
        );
    }
  };

  // Get segmentation actions based on view state
  // Buttons are now rendered inside SegmentTable component
  const getSegmentationActions = (): ReactNode[] => [];

  // Add Segmentation tab when feature flag is enabled and employer has the feature
  useEffect(() => {
    if (flagSegments && hasSegmentsFeature) {
      const segmentationTab: EmployeeDirectoryTabProps = {
        id: 3,
        name: "Segmentation",
        path: "#segmentation",
        content: renderSegmentationContent(),
        actions: getSegmentationActions(),
      };

      setTabs((prev) => {
        // Check if tab already exists
        const existingIndex = prev.findIndex((tab) => tab.id === 3);
        if (existingIndex !== -1) {
          // Update existing tab
          const newTabs = [...prev];
          newTabs[existingIndex] = segmentationTab;
          return newTabs;
        }
        // Insert after Tags tab (id: 1), before Imports tab (id: 2)
        const tagsIndex = prev.findIndex((tab) => tab.id === 1);
        const newTabs = [...prev];
        newTabs.splice(tagsIndex + 1, 0, segmentationTab);
        return newTabs;
      });
    }
  }, [flagSegments, hasSegmentsFeature, segmentView]);

  const location = useLocation();
  const [activeTab, setActiveTab] = useState<EmployeeDirectoryTabProps>(() => {
    const found = tabs.find((it) => it.path === location.hash);
    return found || tabs[0];
  });

  // Keep tab in sync with hash
  useEffect(() => {
    const found = tabs.find((it) => it.path === location.hash);
    if (found && found.id !== activeTab.id) {
      setActiveTab(found);
      // Reset segmentView when switching away from segmentation tab
      if (found.id !== 3) {
        setSegmentView("list");
      }
    }
    // eslint-disable-next-line
  }, [location.hash, tabs]);

  // Keep activeTab in sync when segmentView changes (for segmentation tab)
  useEffect(() => {
    if (activeTab.id === 3) {
      const updatedTab = tabs.find((t) => t.id === 3);
      if (updatedTab) {
        setActiveTab(updatedTab);
      }
    }
  }, [segmentView, tabs]);

  const { isError, error, isLoading, isSuccess, mutate } =
    useEmployeeUploadApiService({
      employer_id: auth?.employer.id ?? 0,
    });

  const [importDataTab, setImportDataTab] = useState(0);

  const onTabChange = (): void => {
    if (fileUploaded) {
      setImportDataTab(() => importDataTab + 1);
    }
  };

  const onUploadSuccess = (): void => {
    setImportDataTab(0);
    setOpened(false);
    setActiveTab(tabs[2]);
  };

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <PageContainer>
      <Drawer
        opened={opened}
        size="full"
        onClose={() => {
          setImportDataTab(0);
          setOpened(false);
        }}
        styles={{
          title: { width: "100%" },
          header: { backgroundColor: "#ffffff" },
        }}
        title={
          <Group
            position="apart"
            spacing="xl"
            style={{
              width: "100%",
            }}
          >
            <Group spacing="xl">
              <ActionIcon onClick={() => setOpened(false)}>
                <ArrowLeft />
              </ActionIcon>
              <Text>Import Employee Data</Text>
            </Group>
            <Group spacing="xl">
              <Button
                onClick={() => {
                  setImportDataTab(0);
                  setFileUploaded(false);
                  setOpened(false);
                }}
                type="button"
                variant="default"
                color="jem-theme"
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                type="button"
                color="jem-theme"
                loading={isLoading}
                onClick={() => onTabChange()}
              >
                Next
              </Button>
            </Group>
          </Group>
        }
        overlayOpacity={0.1}
        overlayBlur={3}
        withCloseButton={false}
        position="right"
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <EmployeeImportForm
            onFileUpload={setFileUploaded}
            activeTab={importDataTab}
            onTabChange={setImportDataTab}
            isError={isError}
            error={error}
            isSuccess={isSuccess}
            mutate={mutate}
            onSuccess={onUploadSuccess}
          />
        </div>
      </Drawer>

      <Drawer
        opened={isTagDrawerOpened}
        size="xl"
        onClose={() => setIsTagDrawerOpened(false)}
        title="Create a Tag"
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <div style={{ padding: "25px", height: "80%", overflow: "auto" }}>
          <TagForm onSuccess={() => setIsTagDrawerOpened(false)} />
        </div>
      </Drawer>

      <PageHeading title="Employees">{activeTab.actions}</PageHeading>
      <Space h="xl" />
      <Tabs
        active={tabs.findIndex((t) => t.id === activeTab.id)}
        onTabChange={(value) => {
          setActiveTab(tabs[value]);
          window.location.hash = tabs[value].path;

          // Reset segmentView when leaving segmentation tab
          if (tabs[value].id !== 3) {
            setSegmentView("list");
          }

          // Clear tag query param if leaving users tab
          const isUsersTab = tabs[value].path === "#users";
          if (!isUsersTab) {
            const url = new URL(window.location.href);
            url.searchParams.delete("tag");
            window.history.replaceState(
              {},
              "",
              url.pathname + url.hash + url.search
            );
          }
        }}
      >
        {tabs.map((it) => (
          <Tabs.Tab key={`#tab-${it.name}`} label={it.name}>
            {it.content}
          </Tabs.Tab>
        ))}
      </Tabs>
    </PageContainer>
  );
};

export default EmployeeDirectoryPage;
